import { DateTime } from 'luxon'

import { type Beneficiary, type BiometricHeight, GenderEnum } from '~/layers/forms/models'

export const useContractConstraints = () => {
  const { RemoteConfigurationStore } = stores()
  const allowCustomerAsInsured =
    RemoteConfigurationStore.value?.form?.aseguradoEsContratante ?? false

  const getTravelMaxDurationDate: (returnDate?: Date | null) => Date | undefined = (returnDate) => {
    return RemoteConfigurationStore.value?.form?.duracionMaximaViaje && returnDate
      ? DateTime.fromJSDate(returnDate)
          .plus({ day: RemoteConfigurationStore.value.form?.duracionMaximaViaje })
          .toJSDate()
      : undefined
  }

  const beneficiariesCountIsMaxed: (list: Beneficiary[]) => boolean = (list) =>
    !!RemoteConfigurationStore.value?.form?.maxBeneficiarios &&
    RemoteConfigurationStore.value.form.maxBeneficiarios <= list.length

  const getBiometricsContraints = (genderCode: PropsTypesOf<typeof GenderEnum>['code'] | null) => {
    let maxWeight: number = 1000
    let minWeight: number = 0
    let maxHeight: BiometricHeight = { feet: 8, inches: 12 }
    let minHeight: BiometricHeight = { feet: 0, inches: 0 }

    if (genderCode) {
      switch (genderCode) {
        case GenderEnum.Feminine.code:
          if (RemoteConfigurationStore.value?.form?.pesoMaximoMujer)
            maxWeight = RemoteConfigurationStore.value?.form?.pesoMaximoMujer
          if (RemoteConfigurationStore.value?.form?.pesoMinimoMujer)
            minWeight = RemoteConfigurationStore.value?.form?.pesoMinimoMujer

          if (RemoteConfigurationStore.value?.form?.estaturaMaximaMujer) {
            const data = RemoteConfigurationStore.value?.form?.estaturaMaximaMujer
              .split("'")
              .filter((i) => i.length)
              .map((i) => +i)
            if (data.length === 2) maxHeight = { feet: data[0] ?? 0, inches: data[1] ?? 0 }
          }
          if (RemoteConfigurationStore.value?.form?.estaturaMinimaMujer) {
            const data = RemoteConfigurationStore.value?.form?.estaturaMinimaMujer
              .split("'")
              .filter((i) => i.length)
              .map((i) => +i)
            if (data.length === 2) minHeight = { feet: data[0] ?? 0, inches: data[1] ?? 0 }
          }
          break
        case GenderEnum.Masculine.code:
          if (RemoteConfigurationStore.value?.form?.pesoMaximoHombre)
            maxWeight = RemoteConfigurationStore.value.form.pesoMaximoHombre
          if (RemoteConfigurationStore.value?.form?.pesoMinimoHombre)
            minWeight = RemoteConfigurationStore.value.form.pesoMinimoHombre

          if (RemoteConfigurationStore.value?.form?.estaturaMaximaHombre) {
            const data = RemoteConfigurationStore.value?.form?.estaturaMaximaHombre
              .split("'")
              .filter((i) => i.length)
              .map((i) => +i)
            if (data.length === 2) maxHeight = { feet: data[0] ?? 0, inches: data[1] ?? 0 }
          }
          if (RemoteConfigurationStore.value?.form?.estaturaMinimaHombre) {
            const data = RemoteConfigurationStore.value?.form?.estaturaMinimaHombre
              .split("'")
              .filter((i) => i.length)
              .map((i) => +i)
            if (data.length === 2) minHeight = { feet: data[0] ?? 0, inches: data[1] ?? 0 }
          }
          break
      }
    }

    return { maxWeight, minWeight, maxHeight, minHeight }
  }

  const constraints = computed(() => {
    if (!RemoteConfigurationStore.value) return {}

    //#region BirthDate
    const maxInsuredBirthDate: Date = new Date()
    let minInsuredBirthDate: Date | undefined = undefined

    if (!RemoteConfigurationStore.value) return

    let maxAge: number | undefined
    let minAge: number | undefined

    switch (RemoteConfigurationStore.value?.ramoEnum) {
      case 'Personas':
        maxAge = RemoteConfigurationStore.value.form?.edadMaximaAsegurado
        minAge = RemoteConfigurationStore.value.form?.edadMinimaAsegurado
        break
      default:
        minAge = RemoteConfigurationStore.value.form?.edadMinima
    }

    if (minAge) maxInsuredBirthDate?.setFullYear(new Date().getFullYear() - minAge)
    if (maxAge) {
      minInsuredBirthDate = new Date()
      minInsuredBirthDate?.setFullYear(new Date().getFullYear() - maxAge)
    }

    //#endregion

    //#region Contact and Address
    const requireCelPhone =
      RemoteConfigurationStore.value.form?.requiereTelCelular &&
      RemoteConfigurationStore.value?.form?.capturaContactos

    const requireEmail =
      RemoteConfigurationStore.value?.form?.requiereCorreoElectronico &&
      RemoteConfigurationStore.value?.form?.capturaContactos
    //#endregion

    //#region Vehicle
    const minVehicleYear = RemoteConfigurationStore.value?.form?.vehiculoYearsAntiguedad
    const allowedVehicleUses = RemoteConfigurationStore.value?.form?.usoVehiculoPermitidos
      ?.replaceAll('_', ' ')
      ?.split(',')
    //#endRegion

    return {
      maxInsuredBirthDate,
      minInsuredBirthDate,
      requireCelPhone,
      requireEmail,
      requireBeneficiaries: RemoteConfigurationStore.value?.form?.requiereBeneficiario,
      ignoreCreditCardCCV: RemoteConfigurationStore.value?.form?.ignoraCVV,
      captureSpecialConditions:
        RemoteConfigurationStore?.value.form?.requiereCondicionesAsegurabilidad,
      captureBiometrics: RemoteConfigurationStore?.value.form?.requiereAntropometricos,
      minVehicleYear,
      allowedVehicleUses,
    }
  })

  return {
    allowCustomerAsInsured,
    getTravelMaxDurationDate,
    beneficiariesCountIsMaxed,
    getBiometricsContraints,
    constraints,
  }
}
